<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getGlobalSettings } from "../getGlobalSettings";
	import type { SolutionsSubcategoryProps } from "./SolutionsSubcategoryProps";
	import racksHero from "../assets/images/racks/racks-hero.jpg";
	import racksHeroSm from "../assets/images/racks/racks-hero.jpg?webp&h=405&w=608&imagetools";
	import racksHeroMd from "../assets/images/racks/racks-hero.jpg?webp&h=470&w=704&imagetools";
	import racksHeroLg from "../assets/images/racks/racks-hero.jpg?webp&h=640&w=960&imagetools";
	import racksHeroXl from "../assets/images/racks/racks-hero.jpg?webp&h=420&w=1424&imagetools";
	import vertinodeAiHero from "../assets/images/vertinode/VertiNode.jpg";
	import vertinodeAiHeroSm from "../assets/images/vertinode/VertiNode.jpg?webp&h=405&w=608&imagetools";
	import vertinodeAiHeroMd from "../assets/images/vertinode/VertiNode.jpg?webp&h=470&w=704&imagetools";
	import vertinodeAiHeroLg from "../assets/images/vertinode/VertiNode.jpg?webp&h=640&w=960&imagetools";
	import vertinodeAiHeroXl from "../assets/images/vertinode/VertiNode.jpg?webp&h=420&w=1424&imagetools";
	import cranesHero from "../assets/images/solutions/cranes.png";
	import cranesHeroSm from "../assets/images/solutions/cranes.png?webp&h=405&w=608&imagetools";
	import cranesHeroMd from "../assets/images/solutions/cranes.png?webp&h=470&w=704&imagetools";
	import cranesHeroLg from "../assets/images/solutions/cranes.png?webp&h=640&w=960&imagetools";
	import cranesHeroXl from "../assets/images/solutions/cranes.png?webp&h=420&w=1424&imagetools";
	import robotsHero from "../assets/images/solutions/robots.png";
	import robotsHeroSm from "../assets/images/solutions/robots.png?webp&h=405&w=608&imagetools";
	import robotsHeroMd from "../assets/images/solutions/robots.png?webp&h=470&w=704&imagetools";
	import robotsHeroLg from "../assets/images/solutions/robots.png?webp&h=640&w=960&imagetools";
	import robotsHeroXl from "../assets/images/solutions/robots.png?webp&h=420&w=1424&imagetools";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import { Category, categoryNameMap } from "../../../core/schema/Category";
	import { nbspify } from "../../../core/utils/nbspify";
	import magnifier from "../../../core/assets/icons/cardsIcons/magnifier.svg?raw";
	import home from "../../../core/assets/icons/cardsIcons/homeHeart.svg?raw";
	import pig from "../../../core/assets/icons/cardsIcons/pig.svg?raw";
	import rabbit from "../../../core/assets/icons/cardsIcons/rabbit.svg?raw";
	import { CardIcon } from "../../../core/schema/categoryTexts";
	import Card from "../components/Card.svelte";
	import { getTranslate } from "../utils/getTranslate";
	import translations from "./SolutionsSubcategory.translations.json";
	import ReferencesSlider from "../components/Sliders/ReferencesSlider.svelte";
	import { getProductDetailPath } from "../../../core/schema/paths/getProductDetailPath.js";
	import Breadcrumbs from "../components/Breadcrumbs.svelte";
	import type { SourceSetQuery } from "../components/Picture/SourceSetQuery.js";
	import { setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";

	type $$Props = SolutionsSubcategoryProps;
	const translate = getTranslate(translations);

	const { TENANT_LOCALE } = getGlobalSettings();
	const { products, category, texts, references, breadcrumbs } = $$restProps as $$Props;

	const cardsIconMap: Record<CardIcon, string> = {
		[CardIcon.Pig]: pig,
		[CardIcon.Rabbit]: rabbit,
		[CardIcon.Magnifier]: magnifier,
		[CardIcon.Home]: home,
	};

	const categoryImageMap = {
		[Category.Cranes]: {
			thumbnail: cranesHero,
			"<md": cranesHeroSm,
			">md": cranesHeroMd,
			">lg": cranesHeroLg,
			">xl": cranesHeroXl,
		},
		[Category.Robots]: {
			thumbnail: robotsHero,
			"<md": robotsHeroSm,
			">md": robotsHeroMd,
			">lg": robotsHeroLg,
			">xl": robotsHeroXl,
		},
		[Category.Racks]: {
			thumbnail: racksHero,
			"<md": racksHeroSm,
			">md": racksHeroMd,
			">lg": racksHeroLg,
			">xl": racksHeroXl,
		},
		[Category.VertinodeAi]: {
			thumbnail: vertinodeAiHero,
			"<md": vertinodeAiHeroSm,
			">md": vertinodeAiHeroMd,
			">lg": vertinodeAiHeroLg,
			">xl": vertinodeAiHeroXl,
		},
	} satisfies Record<
		Category,
		Partial<Record<SourceSetQuery, string>> & {
			thumbnail: string;
		}
	>;

	setContext(thumbnailImageContextKey, categoryImageMap[category].thumbnail);
</script>

<Layout>
	<div class="container">
		<div class="flex items-center overflow-hidden rounded-lg">
			<StaticPicture
				image={categoryImageMap[category][">xl"]}
				alt={categoryNameMap[category][TENANT_LOCALE]}
				width={1424}
				height={420}
				loading="eager"
				class="relative h-full w-full items-center justify-center overflow-hidden xl:flex xl:h-[26.25rem]"
				imgClass="rounded-lg h-full w-full object-cover inset-0 max-w-[initial] xl:absolute"
				sourcesets={categoryImageMap[category]}
			/>
		</div>
	</div>
	<Breadcrumbs {breadcrumbs} />
	<section class="container flex justify-center">
		<div class="lg:w-[50rem]">
			<h1 class="m-0 mb-4 flex flex-col p-0 text-lg font-normal leading-[1.1] md:text-xl lg:mb-10 lg:w-3/4">
				<span class="font-bold">{nbspify(texts.headline[TENANT_LOCALE])}</span>
				{nbspify(texts.subheadline[TENANT_LOCALE])}
			</h1>
			<p class="mb-16 flex flex-col gap-4 text-xs leading-[1.4rem]">
				{#each texts.texts[TENANT_LOCALE] as paragraph}
					<span>{nbspify(paragraph)}</span>
				{/each}
			</p>
			<!-- Out of MVP - no data for these boxes... -->
			<!-- <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
				{#each texts.cards as { icon, title, points }}
					<SubcategorySolutionCard icon={cardsIconMap[icon]} title={title[TENANT_LOCALE]} {points} />
				{/each}
			</div> -->
		</div>
	</section>
	{#if products.length > 0}
		<section class="container">
			<h2 class="mb-10 text-center text-lg md:text-[4rem]">{translate("products")}</h2>
			<div class="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
				{#each products as product}
					<Card
						image={product.primaryImage}
						title={product.name}
						description={product.perex}
						urlSlug={getProductDetailPath(category, product)}
						squaredImage
						containImage
					/>
				{/each}
			</div>
		</section>
	{/if}
	{#if references.length > 0}
		<section class="container">
			<ReferencesSlider {references} />
		</section>
	{/if}
</Layout>
